import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { CalendarHeart, MapPin, Check, HelpCircle, X, PartyPopper } from "lucide-react";
import { useBranding } from "@/lib/branding";

const UD_SEAL = "https://customer-assets-wrfwihn1.emergentagent.net/job_alumni-hub-101/artifacts/n52mfin2_image.png";

const fmt = (s) => {
  if (!s) return "Date TBD";
  const d = new Date(s);
  if (isNaN(d)) return s;
  return d.toLocaleString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
};

const OPTIONS = [
  { value: "attending", label: "Yes, I'll be there", icon: Check, cls: "border-emerald-500 bg-emerald-50 text-emerald-800" },
  { value: "maybe", label: "Maybe", icon: HelpCircle, cls: "border-amber-500 bg-amber-50 text-amber-800" },
  { value: "declined", label: "Can't make it", icon: X, cls: "border-red-400 bg-red-50 text-red-800" },
];

export default function RsvpPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [status, setStatus] = useState("");
  const [guests, setGuests] = useState(0);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    api.get(`/rsvp/${token}`)
      .then((r) => {
        setData(r.data);
        setStatus(r.data.rsvp.status && r.data.rsvp.status !== "invited" ? r.data.rsvp.status : "");
        setGuests(r.data.rsvp.guests || 0);
        setNotes(r.data.rsvp.notes || "");
      })
      .catch((e) => setError(apiError(e.response?.data?.detail)));
  }, [token]);

  const submit = async () => {
    if (!status) { toast.error("Please choose a response"); return; }
    setSaving(true);
    try {
      await api.post(`/rsvp/${token}`, { status, guests: status === "attending" ? guests : 0, guest_names: "", notes });
      setDone(true);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (error) return <Shell><div className="text-center py-10" data-testid="rsvp-error"><X className="h-10 w-10 mx-auto text-red-400 mb-3" /><p className="text-muted-foreground">{error}</p></div></Shell>;
  if (!data) return <Shell><div className="text-center py-10 text-muted-foreground">Loading your invitation…</div></Shell>;

  const e = data.event;
  const loc = e.location || {};

  if (done) return (
    <Shell>
      <div className="text-center py-8" data-testid="rsvp-confirmed">
        <PartyPopper className="h-12 w-12 mx-auto text-accent mb-3" />
        <h2 className="font-serif text-2xl tracking-tight">Thank you, {data.rsvp.name?.split(" ")[0] || "Crusader"}!</h2>
        <p className="text-muted-foreground mt-2">Your response has been recorded: <b className="capitalize text-foreground">{status}</b>.</p>
      </div>
    </Shell>
  );

  return (
    <Shell>
      <div className="text-xs font-mono uppercase tracking-[0.2em] text-accent">You're invited</div>
      <h1 className="font-serif text-3xl tracking-tight mt-1 flex items-start gap-2"><CalendarHeart className="h-7 w-7 text-primary shrink-0 mt-1" />{e.title}</h1>
      <div className="text-sm text-muted-foreground mt-3">{fmt(e.start)}</div>
      {(loc.venue || loc.city) && <div className="flex items-center gap-1.5 text-sm text-muted-foreground mt-1"><MapPin className="h-4 w-4" />{[loc.venue, loc.address, loc.city, loc.state].filter(Boolean).join(", ")}</div>}
      {e.description && <p className="text-sm mt-4 leading-relaxed">{e.description}</p>}
      {e.ticket_price ? <p className="text-sm mt-2">Ticket: <b>${e.ticket_price.toFixed(2)}</b></p> : null}

      <div className="mt-6 pt-6 border-t border-border">
        <div className="text-sm font-medium mb-3">Will you join us?</div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {OPTIONS.map((o) => {
            const Icon = o.icon;
            const active = status === o.value;
            return (
              <button key={o.value} data-testid={`rsvp-option-${o.value}`} onClick={() => setStatus(o.value)}
                className={`flex items-center justify-center gap-2 px-4 py-3 rounded-md border-2 text-sm font-medium transition-all ${active ? o.cls : "border-border text-muted-foreground hover:border-primary/50"}`}>
                <Icon className="h-4 w-4" /> {o.label}
              </button>
            );
          })}
        </div>

        {status === "attending" && (
          <label className="block mt-4">
            <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">How many guests are you bringing?</span>
            <input data-testid="rsvp-guests-input" type="number" min="0" value={guests} onChange={(ev) => setGuests(parseInt(ev.target.value || "0", 10))} className="ud-input mt-1 max-w-[120px]" />
          </label>
        )}

        <label className="block mt-4">
          <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">A note for us (optional)</span>
          <textarea data-testid="rsvp-notes-input" value={notes} onChange={(ev) => setNotes(ev.target.value)} rows={2} className="ud-input mt-1" placeholder="Dietary needs, accessibility, etc." />
        </label>

        <button data-testid="rsvp-submit" onClick={submit} disabled={saving} className="mt-5 w-full px-5 py-3 rounded-md bg-primary text-white text-sm font-medium hover:bg-primary/90 disabled:opacity-60">
          {saving ? "Submitting…" : "Submit RSVP"}
        </button>
      </div>
    </Shell>
  );
}

const Shell = ({ children }) => {
  const b = useBranding();
  return (
  <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
    <div className="w-full max-w-lg">
      <div className="flex items-center gap-3 mb-4">
        <div className="h-12 w-12 rounded-full bg-secondary flex items-center justify-center p-1 shrink-0">{b.logo_primary ? <img src={b.logo_primary} alt="seal" className="h-full w-full object-contain" /> : null}</div>
        <div>
          <div className="font-serif text-lg leading-none tracking-tight text-secondary">{b.university_name}</div>
          <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-accent mt-1">Office of Alumni Relations</div>
        </div>
      </div>
      <div className="bg-card border border-border rounded-lg shadow-sm p-7">{children}</div>
      <p className="text-center text-xs text-muted-foreground mt-4">{b.university_name} Alumni Association</p>
    </div>
  </div>
  );
};
